import {
  columnFiltersToObject,
  objectToColumnFilters,
} from "@/shared/utils/data-table";
import { parseAsString, useQueryState, useQueryStates } from "nuqs";
import { useEffect, useMemo, useRef } from "react";
import type { FiltersStore } from "../stores/dynamic-filters-builder.store";

export function useFiltersSyncWithUrl<
  R extends Record<string, string>,
  K extends keyof R,
  N extends keyof R,
>({
  filtersStore,
  urlKeys,
  rangeColumns,
  numericColumns,
}: {
  filtersStore: FiltersStore;
  urlKeys: R;
  rangeColumns: K[];
  numericColumns: N[];
}) {
  const {
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
  } = filtersStore;

  const columnKeys = useMemo(() => Object.keys(urlKeys), [urlKeys]);

  const filterParams = columnKeys.reduce(
    (acc, key) => {
      acc[key] = parseAsString
        .withDefault("")
        .withOptions({ clearOnDefault: true });
      return acc;
    },
    {} as Record<string, ReturnType<typeof parseAsString.withDefault>>,
  );

  const [filterValues, setFilterValues] = useQueryStates(filterParams, {
    urlKeys,
    clearOnDefault: true,
  });

  const [globalFiltersParam, setGlobalFilterParam] = useQueryState("s", {
    defaultValue: "",
    history: "replace",
    clearOnDefault: true,
  });

  const hydrated = useRef(false);

  // 1. URL → store (hydratation initiale)
  // biome-ignore lint/correctness/useExhaustiveDependencies: adding dep causes infinite loop
  useEffect(() => {
    if (hydrated.current) return;
    setGlobalFilter(globalFiltersParam ?? "");

    // Map URL filter values to Zustand columnFilters format
    const filters = objectToColumnFilters(filterValues, {
      rangeColumns: rangeColumns as string[],
      numericColumns: numericColumns as string[],
    });
    setColumnFilters(filters);
    hydrated.current = true;
  }, [globalFilter, columnFilters, setGlobalFilterParam, setFilterValues]);

  // Sync globalFilter to URL
  useEffect(() => {
    if (!hydrated.current) return;

    setGlobalFilterParam(globalFilter ?? "");
  }, [globalFilter, setGlobalFilterParam]);

  // 2. Store → URL
  useEffect(() => {
    if (!hydrated.current) return;

    // Prepare an object mapping column keys to stringified filter values
    const currentFilterValues = columnFiltersToObject({
      columnKeys,
      columnFilters,
    });
    setFilterValues(currentFilterValues);
  }, [columnKeys, columnFilters, setFilterValues]);

  return {
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
  };
}
